/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description utils for the react router
 */
import _ from 'lodash';
import { generatePath } from 'react-router-dom';

import { ROUTES } from 'configs/routes';
import { WORKLIST_TYPES } from 'app-constants/claims/workLists';
import { CODE_SET_MAPPING } from 'app-constants/codeExplorer';
import { GROUPED_CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import { PAYMENT_TABS } from 'app-constants/payments';
import { ERA_STATE } from 'app-constants/claims/era';

/**
 * Eligibility Requests Tab
 */
const ELIGIBILITY_REQUESTS_TABS = {
	tabKey: 'tab',
	values: {
		all: 'all',
		batch: 'batch',
	},
};

/**
 * Claim Work List Tab
 */
const CLAIM_WORK_LIST_TABS = {
	tabKey: 'tab',
	values: {
		user: 'user',
		all: 'all',
	},
};

/**
 * ERA Tab
 */
const ERA_TABS = {
	tabKey: 'tab',
};

/**
 * Patient Details Tab
 */
const PATIENT_DETAILS_TABS = {
	tabKey: 'tab',
	values: {
		accountsAndClaims: 'accounts-and-claims',
		encounters: 'encounters',
		patientInfo: 'patient-info',
		insurance: 'insurance',
		eligibility: 'eligibility',
	},
};

/**
 * Account Details Tab
 */
const ACCOUNT_DETAILS_TABS = {
	tabKey: 'tab',
	values: {
		claims: 'claims',
		encounters: 'encounters',
	},
};

/**
 * Patient Details Tab keys
 */
const PATIENT_DETAILS_TAB_KEYS = _.values(PATIENT_DETAILS_TABS.values);

/**
 * Account Details Tab keys
 */
const ACCOUNT_DETAILS_TAB_KEYS = _.values(ACCOUNT_DETAILS_TABS.values);

/**
 * Eligibility Requests Tab keys
 */
const ELIGIBILITY_REQUESTS_TAB_KEYS = _.values(ELIGIBILITY_REQUESTS_TABS.values);

/**
 * Claim Work List Tab keys
 */
const CLAIM_WORK_LIST_TAB_KEYS = _.values(CLAIM_WORK_LIST_TABS.values);

/**
 * ERA Tab keys
 */
const ERA_TAB_KEYS = _.values(ERA_STATE);

/**
 * Payment Tab keys
 */
const PAYMENT_TAB_KEYS = _.values(PAYMENT_TABS.values);

/**
 * query param keys for entities
 */
const searchParamsKeys = {
	claimStatusRequestId: 'claimStatusId',
	coverageId: 'coverageId',
	eligibilityRequestId: 'eligibilityId',
	helpQuestion: 'qn',
	worklistId: 'id',
	guarantorId: 'guarantorId',
	batchPostPaymentId: 'batchPostPaymentId',
	batchPostPaymentGroupedStatus: 'batchPostPaymentGroupedStatus',
	batchPostPaymentCompletedTimestamp: 'batchPostPaymentCompletedTimestamp',
	encounterId: 'encounterId',
	patientId: 'patientId',
};

const newRequestKeys = {
	singlePatient: 'single-patient',
	batch: 'batch-upload',
};

/**
 * get logout options for keycloak api
 * @returns {Record<string,string>} logout options
 */
const getLogoutOptions = (): Record<'redirectUri', string> => {
	return {
		redirectUri: globalThis.decodeURIComponent(
			generatePath(ROUTES.logout.redirect, {
				origin: globalThis.location.origin,
			}),
		),
	};
};

/**
 * get home path
 * @returns {string} path
 */
const getHomePath = (): string => {
	return generatePath(ROUTES.home.landing);
};

/**
 * get logout path
 * @returns {string} path
 */
const getLogoutPath = (): string => {
	return generatePath(ROUTES.logout.landing);
};

/**
 * eligibility request create path
 * @returns {string} path
 */
const getEligibilityNewRequestPath = (): string => {
	return generatePath(ROUTES.eligibility.newRequest);
};

/**
 * Edit eligibility request create path
 * @param {string} id
 * @returns {string} path
 */
const getEditEligibilityRequestPath = (id: string): string => {
	return generatePath(ROUTES.eligibility.editRequest, { id });
};

const getEditClaimEligibilityRequestPath = (id: string): string => {
	const pagePath = generatePath(ROUTES.claims.editEligibilityRequest);
	const searchParams = `?${searchParamsKeys.coverageId}=${id}`;
	return `${pagePath}${searchParams}`;
};

/**
 * View eligibility response path
 * @param {string} id
 * @returns {string} path
 */
const getEligibilityResponsePath = (id: string): string => {
	return generatePath(ROUTES.eligibility.viewResponse, { id });
};

const getClaimEligibilityPath = (id: string): string => {
	return generatePath(ROUTES.claims.eligibilityResponse, { id });
};

/**
 * Get patient eligibility new request path
 * @param {string} patientId - Patient ID for context
 * @returns {string} path with patientId query param
 */
const getPatientEligibilityNewRequestPath = (patientId: string): string => {
	const basePath = generatePath(ROUTES.claims.newEligibilityRequest);
	return `${basePath}?${searchParamsKeys.patientId}=${patientId}`;
};

/**
 * get edit batch upload path
 * @returns {string} path
 */
const getEditBatchRequestPath = (id: string): string => {
	const path = ROUTES.eligibility.batch.edit;
	return generatePath(path, { id });
};

/**
 * get batch response path
 * @returns {string} path
 */
const getBatchResponsePath = (id: string): string => {
	const path = ROUTES.eligibility.batch.viewResponse;
	return generatePath(path, { id });
};

/**
 * Validate Eligibility Requests Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validateEligibilityRequestsTab = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string | undefined => {
	if (_.includes(ELIGIBILITY_REQUESTS_TAB_KEYS, tab)) {
		return tab;
	}
	if (returnDefault) {
		return _.first(ELIGIBILITY_REQUESTS_TAB_KEYS);
	}
	return undefined;
};

/**
 * get eligibility response path
 * @returns {string} path
 */
const getEligibilityRequestsPath = ({
	tab,
	returnDefault,
	coverageId,
}: {
	tab?: string;
	returnDefault?: boolean;
	coverageId?: string;
}): string => {
	const pagePath = generatePath(ROUTES.eligibility.responses, {
		[ELIGIBILITY_REQUESTS_TABS.tabKey]: validateEligibilityRequestsTab({ tab, returnDefault }),
	});
	if (coverageId) {
		const searchParams = `?${searchParamsKeys.coverageId}=${encodeURIComponent(coverageId)}`;
		return `${pagePath}${searchParams}`;
	}
	return pagePath;
};

/**
 * eligibility batch request create path
 * @returns {string} path
 */
const getBatchEligibilityRequestPath = (id?: string): string => {
	return generatePath(ROUTES.eligibility.batch.add, { id });
};

/**
 * getEligibilityTabPath
 * @param {string} tab
 * @returns {string} path
 */
const getNewEligibilityRequestPath = ({
	tab,
	claimStatusRequestId,
	coverageId,
}: {
	tab?: string;
	claimStatusRequestId?: string;
	coverageId?: string;
}): string => {
	if (claimStatusRequestId) {
		const pagePath = generatePath(ROUTES.eligibility.newRequest, { tab: newRequestKeys.singlePatient });
		const searchParams = `?${searchParamsKeys.claimStatusRequestId}=${encodeURIComponent(claimStatusRequestId)}`;
		return `${pagePath}${searchParams}`;
	}
	if (coverageId) {
		const pagePath = generatePath(ROUTES.claims.newEligibilityRequest, { tab });
		const searchParams = `?${searchParamsKeys.coverageId}=${encodeURIComponent(coverageId)}`;
		return `${pagePath}${searchParams}`;
	}

	return generatePath(ROUTES.eligibility.newRequest, { tab });
};

/**
 * get claim status ids from search params
 * @param {string} search
 * @returns {string[] | undefined} claimStatusIDs
 */
const getClaimsIdsFromSearch = (search: string): string[] | undefined => {
	const searchParams = new URLSearchParams(search)?.get(searchParamsKeys.claimStatusRequestId);
	const claimStatusIDs = _.compact(_.split(searchParams, ','));

	if (!_.isEmpty(claimStatusIDs)) {
		return claimStatusIDs;
	}
};

/**
 * New Claim Status request create path
 * @returns {string} path
 */
const getClaimsNewRequestPath = (): string => {
	return generatePath(ROUTES.claims.newRequest);
};

/**
 * Resubmit Claim Status request create path
 * @returns {string} path
 */
const getClaimsResubmitRequestPath = (id: string): string => {
	return generatePath(ROUTES.claims.resubmit, { id });
};

/**
 * File Primary Claim path
 * @returns {string} path
 */
const getFilePrimaryClaimPath = (type: ClaimFiling.IClaimType): string => {
	return generatePath(ROUTES.claims.filePrimaryClaim, { type });
};

/**
 * Edit Draft Claim path
 * @returns {string} path
 */
const getEditDraftClaimPath = (type: ClaimFiling.IClaimType, draftId: string): string => {
	return `${generatePath(ROUTES.claims.filePrimaryClaim, { type })}?draftId=${draftId}`;
};

/**
 * get Draft Claims path
 * @returns {string} path
 */
const getDraftClaimsPath = (): string => {
	return generatePath(ROUTES.claims.draftClaims);
};

/**
 * Review Secondary Claim request create path
 * @returns {string} path
 */
const getClaimsReviewSecondaryClaimPath = (id: string): string => {
	return generatePath(ROUTES.claims.reviewSecondaryClaim, { id });
};

/**
 * View claim status response path
 * @param {string} id
 * @returns {string} path
 */
const getClaimResponsePath = (id: string): string => {
	return generatePath(ROUTES.claims.viewResponse, { id });
};

/**
 * Validate Claim  Requests Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validateClaimRequestsTab = ({
	tab,
	returnDefault,
	isBillingCustomer = true,
}: {
	tab?: string;
	returnDefault?: boolean;
	isBillingCustomer?: boolean;
}): string | undefined => {
	const claimWorkStateKeys = _.keys(GROUPED_CLAIM_WORK_STATES);
	if (!isBillingCustomer) {
		return _.first(claimWorkStateKeys);
	}
	if (_.includes(claimWorkStateKeys, tab)) {
		return tab;
	}
	if (returnDefault) {
		return _.first(claimWorkStateKeys);
	}
	return undefined;
};

/**
 * claim status response list path
 * @returns {string} path
 */
const getClaimsRequestPath = ({
	tab,
	returnDefault,
	idsList,
	batchPostPaymentMeta,
}: {
	tab?: string;
	returnDefault?: boolean;
	idsList?: string[];
	batchPostPaymentMeta?: {
		status: PatientPayment.IPostPaymentGroupedStatus;
		batchId: string;
		completedTimestamp: string;
	};
} = {}): string => {
	const basePath = generatePath(ROUTES.claims.allResponses, {
		[ELIGIBILITY_REQUESTS_TABS.tabKey]: validateClaimRequestsTab({ tab, returnDefault }),
	});
	let searchParams = '';
	if (idsList) {
		searchParams = `?${searchParamsKeys.claimStatusRequestId}=${encodeURIComponent(_.join(idsList, ','))}`;
	}
	if (batchPostPaymentMeta) {
		searchParams = `?${searchParamsKeys.batchPostPaymentId}=${encodeURIComponent(batchPostPaymentMeta?.batchId)}&${searchParamsKeys.batchPostPaymentGroupedStatus}=${encodeURIComponent(batchPostPaymentMeta?.status)}&${searchParamsKeys.batchPostPaymentCompletedTimestamp}=${encodeURIComponent(batchPostPaymentMeta?.completedTimestamp)}`;
	}
	return `${basePath}${searchParams}`;
};

/**
 * Edit claim status request path
 * @param {string} id
 * @returns {string} path
 */
const getEditClaimRequestPath = (id: string): string => {
	const path = ROUTES.claims.editRequest;
	return generatePath(path, { id });
};

/**
 * claim Status Input Form Path
 * @returns {string} path
 */
const claimStatusInputFormPath = (eligibilityRequestId?: string): string => {
	const pagePath = generatePath(ROUTES.claims.newRequest);
	const searchParams = eligibilityRequestId
		? `?${searchParamsKeys.eligibilityRequestId}=${encodeURIComponent(eligibilityRequestId)}`
		: '';
	return `${pagePath}${searchParams}`;
};

/**
 * New Claim Work List create path
 * @returns {string} path
 */
const getNewWorkListPath = (type: ClaimWorkLists.IWorkListType, id?: string): string => {
	const path = generatePath(type === WORKLIST_TYPES.auto.key ? ROUTES.claims.worklist.add : ROUTES.claims.worklist.addManual);
	const searchParams = id ? `?${searchParamsKeys.worklistId}=${encodeURIComponent(id)}` : '';
	return `${path}${searchParams}`;
};

/**
 * Validate Claim Work List Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validateClaimWorkListTab = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string | undefined => {
	if (_.includes(CLAIM_WORK_LIST_TAB_KEYS, tab)) {
		return tab;
	}
	if (returnDefault) {
		return _.first(CLAIM_WORK_LIST_TAB_KEYS);
	}
	return undefined;
};

/**
 * get claim work lists path
 * @returns {string} path
 */
const getClaimWorkListsPath = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string => {
	return generatePath(ROUTES.claims.worklist.listView, {
		[CLAIM_WORK_LIST_TABS.tabKey]: validateClaimWorkListTab({ tab, returnDefault }),
	});
};

/**
 * Edit claim work list path
 * @param {string} id
 * @returns {string} path
 */
const getEditClaimWorkListPath = (id: string): string => {
	return generatePath(ROUTES.claims.worklist.edit, { id });
};

/**
 * claim work list response path
 * @param {string} id
 * @returns {string} path
 */
const getClaimWorkListResponsePath = ({ id, tab, returnDefault = true }: { id: string; tab?: string; returnDefault?: boolean }): string => {
	return generatePath(ROUTES.claims.worklist.viewResponse, {
		id,
		[CLAIM_WORK_LIST_TABS.tabKey]: validateClaimRequestsTab({ tab, returnDefault }),
	});
};

/**
 * Edit eligibility request create path
 * @param {string} id
 * @returns {string} path
 */
const getEditPayerDetailsPath = (id: string, tab?: string): string => {
	return generatePath(ROUTES.settings.dataManagement.payers.edit, { id, tab });
};

/**
 * Payers path
 * @returns {string} path
 */
const getManagePayersPath = (id?: string): string => {
	const path = generatePath(ROUTES.settings.dataManagement.payers.listView);
	if (id) {
		return `${path}#${id}`;
	}
	return path;
};

/**
 * AddProvider create path
 * @returns {string} path
 */
const getAddProviderPath = (): string => {
	return generatePath(ROUTES.settings.dataManagement.providers.add);
};

/**
 * EditProvider create path
 * @param {string} npi
 * @returns {string} path
 */
const getEditProviderPath = (npi: string): string => {
	return generatePath(ROUTES.settings.dataManagement.providers.edit, { id: npi });
};

/**
 * get provider path
 * @returns {string} path
 */
const getProviderPath = (id?: string): string => {
	const path = ROUTES.settings.dataManagement.providers.listView;
	if (id) {
		return `${path}#${id}`;
	}
	return path;
};

/**
 * AddLocations create path
 * @returns {string} path
 */
const getAddLocationPath = (): string => {
	return generatePath(ROUTES.settings.dataManagement.locations.add);
};

/**
 * EditLocation create path
 * @param {string} id
 * @returns {string} path
 */
const getEditLocationPath = (id: string): string => {
	return generatePath(ROUTES.settings.dataManagement.locations.edit, { id });
};

/**
 * get location path
 * @returns {string} path
 */
const getLocationPath = (id?: string): string => {
	const path = ROUTES.settings.dataManagement.locations.listView;
	if (id) {
		return `${path}#${id}`;
	}
	return generatePath(path);
};

/**
 * AddKeycloakUser Path
 * @returns {string} path
 */
const getAddKeycloakUserPath = (): string => {
	return generatePath(ROUTES.settings.organization.users.add);
};

/**
 * EditKeycloakUser create path
 * @param {string} id
 * @returns {string} path
 */
const getEditKeycloakUserPath = (id: string): string => {
	return generatePath(ROUTES.settings.organization.users.edit, { id });
};

// todo
/**
 * Subscriptions path
 * @param {string} tab
 * @returns {string} path
 */
const getSubscriptionsPath = (tab?: string): string => {
	return generatePath(ROUTES.settings.organization.subscriptions, { tab });
};

/**
 * Settings path
 * @returns {string} path
 */
const getSettingsPath = (): string => {
	return generatePath(ROUTES.settings.landing);
};

/**
 * get help path
 * @returns {string} path
 */
const getUserHelpPath = (key?: string): string => {
	const searchParams = key ? `?${searchParamsKeys.helpQuestion}=${encodeURIComponent(key)}` : '';
	return `${generatePath(ROUTES.help.landing)}${searchParams}`;
};

const getAllCodesPath = (): string => {
	return generatePath(ROUTES.codeExplorer.allCodes);
};

/**
 * get code search results path
 * @param {string} searchTerm
 * @returns {string} path
 */
const getCodeSearchResultsPath = (searchTerm: string): string => {
	return generatePath(ROUTES.codeExplorer.searchResults, { searchTerm: encodeURIComponent(searchTerm) });
};

/**
 * get code set search results path
 * @returns {string} path
 */
const getCodeSetSearchResultsPath = (params: { codeSet: CodeExplorer.ICodeSetSystem; searchTerm: string }): string => {
	return generatePath(ROUTES.codeExplorer.codeSetSearchResults, params);
};

/**
 * get code list search results path
 * @param {string} searchTerm
 * @returns {string} path
 */
const getCodeListSearchResultsPath = ({
	searchTerm,
	// TODO : add dynamic code set later
	codeSet = CODE_SET_MAPPING.ICD10PCS,
}: {
	searchTerm: string;
	codeSet?: CodeExplorer.ICodeSetSystem;
}): string => {
	return generatePath(ROUTES.codeExplorer.codeList, { codeSet, searchTerm });
};

/**
 * get favorite code search results path
 * @param {string} searchTerm
 * @returns {string} path
 */
const getFavoriteCodeSearchResultsPath = (searchTerm: string): string => {
	return generatePath(ROUTES.codeExplorer.favoriteCodes, { searchTerm: encodeURIComponent(searchTerm) });
};

/**
 * get code detailed view path
 * @returns {string} path
 */
const getCodeDetailedViewPath = ({ codeSet, code, tab }: { codeSet: CodeExplorer.ICodeSetSystem; code: string; tab?: string }): string => {
	return generatePath(ROUTES.codeExplorer.codeDetails, { codeSet, code, tab });
};

/**
 * Validate Payment Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validatePaymentTab = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string | undefined => {
	if (_.includes(PAYMENT_TAB_KEYS, tab)) {
		return tab;
	}
	if (returnDefault) {
		return PAYMENT_TABS.values.unallocated;
	}
	return undefined;
};

const getPaymentsPath = ({ tab, returnDefault, guarantorId }: { tab?: string; returnDefault?: boolean; guarantorId?: string }): string => {
	const path = generatePath(ROUTES.payments.allPayments, { [PAYMENT_TABS.tabKey]: validatePaymentTab({ tab, returnDefault }) });
	if (guarantorId) {
		return `${path}?${searchParamsKeys.guarantorId}=${encodeURIComponent(guarantorId)}`;
	}
	return path;
};

const getRecordPaymentPath = (): string => {
	return generatePath(ROUTES.payments.recordPayment);
};

const getEditPaymentDetailsPath = (id: string): string => {
	return generatePath(ROUTES.payments.editPaymentDetails, { id });
};

const getPaymentAllocationPath = (id: string): string => {
	return generatePath(ROUTES.payments.paymentAllocation, { id });
};

/**
 * get payments integration path
 * @returns {string} path
 */
const getPaymentsIntegrationPath = (): string => {
	return generatePath(ROUTES.settings.integration.payment);
};

/**
 * Validate Patient Details Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validatePatientDetailsTab = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string | undefined => {
	if (_.includes(PATIENT_DETAILS_TAB_KEYS, tab)) {
		return tab;
	}
	if (returnDefault) {
		return _.first(PATIENT_DETAILS_TAB_KEYS);
	}
	return undefined;
};

/**
 * Validate Account Details Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validateAccountDetailsTab = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string | undefined => {
	if (_.includes(ACCOUNT_DETAILS_TAB_KEYS, tab)) {
		return tab;
	}
	if (returnDefault) {
		return _.first(ACCOUNT_DETAILS_TAB_KEYS);
	}
	return undefined;
};

/**
 * get patient details path
 * @returns {string} path
 */
const getPatientDetailsPath = ({
	id,
	tab,
	returnDefault = true,
	encounterId,
}: {
	id: string;
	tab?: string;
	returnDefault?: boolean;
	encounterId?: string;
}): string => {
	const path = generatePath(ROUTES.patients.PatientDetails, {
		id,
		[PATIENT_DETAILS_TABS.tabKey]: validatePatientDetailsTab({ tab, returnDefault }),
	});
	if (encounterId) {
		return `${path}?${searchParamsKeys.encounterId}=${encodeURIComponent(encounterId)}`;
	}
	return path;
};

/**
 * get guarantor path
 * @returns {string} path
 */
const getGuarantorPath = (id: string): string => {
	return generatePath(ROUTES.accounts.guarantor, { id });
};

/**
 * get account details path
 * @returns {string} path
 */
const getAccountDetailsPath = (id: string, tab?: string): string => {
	return generatePath(ROUTES.accounts.accountDetails, { id, tab });
};

/**
 * get era response path
 * @returns {string} path
 */
const getEraResponsePath = ({ id, tab, returnDefault = true }: { id: string; tab?: string; returnDefault?: boolean }): string => {
	return generatePath(ROUTES.payments.era.viewResponse, {
		id,
		[CLAIM_WORK_LIST_TABS.tabKey]: validateClaimRequestsTab({ tab, returnDefault }),
	});
};

/**
 *Clearinghouse list path
 * @returns {string} path
 */
const getClearinghousePath = (): string => {
	return generatePath(ROUTES.settings.integration.clearingHouse.listView);
};

/**
 * AddClearinghouse create path
 * @returns {string} path
 */
const getAddClearinghousePath = (): string => {
	return generatePath(ROUTES.settings.integration.clearingHouse.add);
};

/**
 * EditClearinghouse  path
 * @param {string} id
 * @returns {string} path
 */
const getEditClearinghousePath = (id: string): string => {
	return generatePath(ROUTES.settings.integration.clearingHouse.edit, { id });
};

/* Validate ERA Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validateERATab = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string | undefined => {
	if (_.includes(ERA_TAB_KEYS, tab)) {
		return tab;
	}
	if (returnDefault) {
		return _.first(ERA_TAB_KEYS);
	}
	return undefined;
};

const getERAPath = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string => {
	return generatePath(ROUTES.payments.era.listView, { [ERA_TABS.tabKey]: validateERATab({ tab, returnDefault }) });
};

/* get NPI Registry details path
 * @param {string} npi
 * @returns {string} path
 */
const getNpiRegistryDetailsPath = (npi: string): string => {
	return `https://npiregistry.cms.hhs.gov/provider-view/${npi}`;
};

/**
 * get add custom attribute path
 * @returns {string} path
 */
const getAddCustomAttributePath = (): string => {
	return generatePath(ROUTES.settings.dataManagement.customAttributes.add);
};

/**
 * get edit custom attribute path
 * @param {string} id
 * @returns {string} path
 */
const getEditCustomAttributePath = (id: string): string => {
	return generatePath(ROUTES.settings.dataManagement.customAttributes.edit, { id });
};

/**
 * get custom attributes path
 * @returns {string} path
 */
const getCustomAttributesPath = (): string => {
	return generatePath(ROUTES.settings.dataManagement.customAttributes.listView);
};

export {
	ELIGIBILITY_REQUESTS_TABS,
	CLAIM_WORK_LIST_TABS,
	PAYMENT_TABS,
	ELIGIBILITY_REQUESTS_TAB_KEYS,
	validateEligibilityRequestsTab,
	searchParamsKeys,
	newRequestKeys,
	getLogoutOptions,
	getLogoutPath,
	getHomePath,
	getEditEligibilityRequestPath,
	getEligibilityResponsePath,
	getEditPayerDetailsPath,
	getAddProviderPath,
	getEditProviderPath,
	getProviderPath,
	getAddLocationPath,
	getEditLocationPath,
	getLocationPath,
	getAddKeycloakUserPath,
	getEditKeycloakUserPath,
	getEditBatchRequestPath,
	getBatchResponsePath,
	getEligibilityRequestsPath,
	getEligibilityNewRequestPath,
	getBatchEligibilityRequestPath,
	getNewEligibilityRequestPath,
	getClaimsIdsFromSearch,
	getClaimsRequestPath,
	getClaimsNewRequestPath,
	getClaimsResubmitRequestPath,
	getEditClaimRequestPath,
	getClaimResponsePath,
	getSubscriptionsPath,
	getSettingsPath,
	getManagePayersPath,
	getUserHelpPath,
	claimStatusInputFormPath,
	getNewWorkListPath,
	getEditClaimWorkListPath,
	validateClaimWorkListTab,
	getClaimWorkListsPath,
	getClaimWorkListResponsePath,
	getAllCodesPath,
	getCodeSearchResultsPath,
	getCodeSetSearchResultsPath,
	getCodeDetailedViewPath,
	getFavoriteCodeSearchResultsPath,
	getCodeListSearchResultsPath,
	getPaymentsIntegrationPath,
	validateClaimRequestsTab,
	getPatientDetailsPath,
	getGuarantorPath,
	getAccountDetailsPath,
	getRecordPaymentPath,
	getEditPaymentDetailsPath,
	getPaymentAllocationPath,
	getPaymentsPath,
	validatePaymentTab,
	getEraResponsePath,
	getAddClearinghousePath,
	getEditClearinghousePath,
	getClearinghousePath,
	validateERATab,
	getERAPath,
	getClaimsReviewSecondaryClaimPath,
	getFilePrimaryClaimPath,
	getEditDraftClaimPath,
	getNpiRegistryDetailsPath,
	getDraftClaimsPath,
	getAddCustomAttributePath,
	getEditCustomAttributePath,
	getCustomAttributesPath,
	PATIENT_DETAILS_TABS,
	validatePatientDetailsTab,
	ACCOUNT_DETAILS_TABS,
	validateAccountDetailsTab,
	getClaimEligibilityPath,
	getPatientEligibilityNewRequestPath,
	getEditClaimEligibilityRequestPath,
};
